import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import * as Sentry from '@sentry/angular-ivy';
import { AppToastComponent, AppToastData, AppToastVariant } from './app-toast.component';
import { CapturedHttpError, takeRecentHttpError } from './http-error-capture.interceptor';

const ERROR_PATTERN = /fail|error|erro|falh|invalid|inv[aá]lid|not found|unable|could not|cannot|n[aã]o foi|n[aã]o [eé] poss[ií]vel/i;
const SUCCESS_PATTERN = /success|sucesso|updated|saved|deleted|removed|created|applied|atualizad|salv|exclu[ií]d|criad|conclu[ií]d/i;

/** "Failed", "Error!", "Erro" — avisos que não dizem o que aconteceu. */
const GENERIC_ERROR = /^\s*(failed|error|erro|falha|falhou)\s*[.!]*\s*$/i;

const DEFAULT_DURATION = 4000;

const SUPPORT_HINT = 'This action could not be completed. Try again; if it keeps happening, contact support.';

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

function variantOf(message: string): AppToastVariant {
	if (ERROR_PATTERN.test(message)) return 'error';
	if (SUCCESS_PATTERN.test(message)) return 'success';
	return 'info';
}

function shortUrl(url: string): string {
	try {
		const { pathname } = new URL(url, window.location.origin);
		return pathname.replace(/^.*?\/api\//, '/');
	} catch {
		return url;
	}
}

/** Nome de coluna do banco para o que a pessoa vê na tela. */
const FIELD_LABELS: Record<string, string> = {
	holeName: 'hole name',
	padId: 'program name',
	sampleCode: 'sample code',
};

/**
 * Tira da mensagem o que é do interior do sistema: código HTTP, rota, ID (UUID),
 * nome de coluna. As regras do banco (triggers) voltam em texto técnico; as
 * conhecidas viram uma frase para a pessoa. Sem nada legível, devolve null.
 */
export function sanitizeMessage(message: string | null | undefined): string | null {
	let text = (message ?? '').toString().trim();
	if (!text) return null;

	// Erro cru do Angular: "Http failure response for https://…/api/x: 400 OK".
	if (/http failure response|unknown error/i.test(text)) return null;

	const unique = /Value (.+?) for column (\w+) must be unique within the site/i.exec(text);
	if (unique) {
		const field = FIELD_LABELS[unique[2]] ?? 'value';
		return `The ${field} ${unique[1]} already exists in this site. Use another one.`;
	}

	const overlap = /^(\w+) intervals cannot overlap in the same collar/i.exec(text);
	if (overlap) {
		return `This ${overlap[1]} interval overlaps another interval of the same hole.`;
	}
	if (/must not overlap in the same collar/i.test(text)) {
		return 'This depth is already used in the same hole.';
	}

	text = text
		.replace(/\(\s*\w*Id\s*:\s*[^)]*\)/gi, '')
		.replace(UUID, '')
		.replace(/\bOverlapping IDs:.*$/gim, '')
		.replace(/\s{2,}/g, ' ')
		.replace(/\s+([.,;:])/g, '$1')
		.trim();
	return text || null;
}

/** Monta título e mensagem do aviso a partir do que a tela pediu. */
export function buildToastData(message: string, action?: string, config?: MatSnackBarConfig): AppToastData {
	const original = (message ?? '').toString().trim();
	const variant = variantOf(original);
	const hasAction = !!action;

	if (variant !== 'error') {
		return {
			variant,
			title: variant === 'success' ? 'Success' : 'Notice',
			message: sanitizeMessage(original) ?? original,
			actionLabel: action || null,
			hasAction,
		};
	}

	// Erro: o motivo real vem da última resposta da API, quando ela acabou de falhar.
	const http = takeRecentHttpError();
	const raw = http?.message && !/^ocorreu um erro inesperado\.?$/i.test(http.message) ? http.message : null;
	const hidden = !!raw && isSensitive(raw);
	if (http && (hidden || http.status >= 500 || !raw)) {
		// Detalhe técnico (status, rota, mensagem do backend) só no log, nunca na tela.
		reportToLog(http, original);
	}
	const backend = hidden ? null : sanitizeMessage(raw);
	const text = sanitizeMessage(original);
	const generic = !text || GENERIC_ERROR.test(text);

	let body: string;
	if (generic) {
		body = backend ?? SUPPORT_HINT;
	} else {
		body = backend && !text.includes(backend) ? `${text}\n${backend}` : text;
	}

	return {
		variant,
		title: 'Error',
		message: body,
		actionLabel: 'Confirm',
		hasAction,
	};
}

/**
 * Mensagem que expõe o interior do sistema: SQL, Prisma, stack trace, caminho de
 * arquivo, conexão, credencial. Essa não vai para a tela; vai para o log.
 */
const SENSITIVE_PATTERN = new RegExp(
	[
		'prisma', 'invocation', '\\bsql\\b', 'select\\s.+\\sfrom', 'insert into', 'update\\s.+\\sset', 'constraint',
		'relation ".+"', 'column ".+"', 'syntax error', 'ECONN', 'ETIMEDOUT', 'ENOTFOUND', 'socket',
		'\\bat\\s+\\S+\\s+\\(', 'node_modules', '/usr/app', '\\.ts:\\d+', '\\.js:\\d+', 'stack',
		'password', 'passwd', 'secret', 'token', 'bearer', 'authorization', 'connection string',
		'postgres', 'redis', 'camunda', 'engine-rest', 'keycloak', 'TypeError', 'ReferenceError',
		'Cannot read propert', 'undefined', 'null value', 'internal server error',
	].join('|'),
	'i',
);

/**
 * O limite é por linha, não pelo total: retorno de importação com várias linhas
 * ("Linha 5: …") é útil para a pessoa e não deve ser escondido como stack trace.
 */
function isSensitive(message: string): boolean {
	if (message.length > 2000 || SENSITIVE_PATTERN.test(message)) return true;
	return message.split('\n').some((line) => line.length > 400);
}

function reportToLog(http: CapturedHttpError, screenMessage: string): void {
	const context = { screenMessage, method: http.method, url: http.url, status: http.status, backendMessage: http.message };
	console.error('[app-toast]', context);
	try {
		Sentry.captureMessage(`HTTP ${http.status} ${http.method} ${shortUrl(http.url)}`, {
			level: 'error',
			extra: context,
		});
	} catch {
		// Sem Sentry configurado o console basta.
	}
}

let installed = false;

/**
 * Aviso com `panelClass` próprio mantém o visual dele. Todo o resto, a faixa
 * preta padrão, vira o cartão.
 */
function hasOwnStyle(config?: MatSnackBarConfig): boolean {
	return !!config?.panelClass && [config.panelClass].flat().filter(Boolean).length > 0;
}

/**
 * Todo `snackBar.open(...)` sem estilo próprio passa a abrir o AppToastComponent
 * no canto inferior direito, em vez da faixa preta padrão.
 *
 * Vai no protótipo porque o MatSnackBar do Angular Material 14 é fornecido por
 * módulo: trocar o provider na raiz não chega às telas lazy, e editar as ~700
 * chamadas uma a uma não é viável.
 *
 * Erro fica na tela até a pessoa confirmar ou fechar; os demais somem sozinhos.
 */
export function installAppToast(): void {
	if (installed) return;
	installed = true;

	const originalOpen = MatSnackBar.prototype.open;

	MatSnackBar.prototype.open = function (
		this: MatSnackBar,
		message: string,
		action = '',
		config?: MatSnackBarConfig,
	): MatSnackBarRef<TextOnlySnackBar> {
		if (hasOwnStyle(config)) {
			return originalOpen.call(this, message, action, config);
		}
		const data = buildToastData(message, action, config);
		const ref = this.openFromComponent(AppToastComponent, {
			...config,
			data,
			duration: data.variant === 'error' ? undefined : (config?.duration ?? DEFAULT_DURATION),
			horizontalPosition: 'end',
			verticalPosition: 'bottom',
			panelClass: ['app-toast-panel'],
			politeness: data.variant === 'error' ? 'assertive' : 'polite',
		});
		return ref as unknown as MatSnackBarRef<TextOnlySnackBar>;
	};
}
